import { faPaperPlane, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useState } from 'react';

import { Color } from '@/constants/color';
import {
  useCeleryUnacked,
  usePurgeUnacked,
  useRemoveUnacked,
  useRestoreUnacked
} from '@/hooks/useCeleryConsole';
import { UnackedEntry } from '@/types/celeryConsole';
import Button from '@/ui/components/Button';
import Tooltip from '@/ui/components/Tooltip';

import { Loading, PanelCard, PanelError } from '../../../shared';
import { PollInterval } from '../../../types';
import BadgeFilterRow from '../components/BadgeFilterRow';
import BulkActionBar from '../components/BulkActionBar';
import CopyableField from '../components/CopyableField';
import NameFilter from '../components/NameFilter';
import QueueBadge, { QUEUE_NAMES } from '../components/QueueBadge';
import TaskCell from '../components/TaskCell';
import { useToggleSet } from '../hooks/useToggleSet';

const DETAIL_COL_SPAN = 7;

const startedAtIso = (ageSeconds: number | null): string | null => {
  if (ageSeconds == null) return null;
  return new Date(Date.now() - ageSeconds * 1000).toLocaleString();
};

const UnackedDetailRow = ({ entry, colSpan }: { entry: UnackedEntry; colSpan: number }) => {
  const started = startedAtIso(entry.age_seconds);
  return (
    <tr className="bg-gray-50">
      <td colSpan={colSpan} className="px-3 py-2">
        <div className="text-xs space-y-1">
          {entry.argsrepr && (
            <CopyableField label="args" value={entry.argsrepr} className="font-mono" />
          )}
          {entry.kwargsrepr && (
            <CopyableField label="kwargs" value={entry.kwargsrepr} className="font-mono" />
          )}
          {started && (
            <div>
              <span className="text-gray-500">started:</span> {started}
            </div>
          )}
          {entry.retries > 0 && (
            <div>
              <span className="text-gray-500">retries:</span> {entry.retries}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

const UnackedPanel = ({ interval }: { interval: PollInterval }) => {
  const { data, isLoading, error } = useCeleryUnacked(interval);
  const restore = useRestoreUnacked();
  const remove = useRemoveUnacked();
  const expanded = useToggleSet<string>();
  const queues = useToggleSet<string>();
  const selected = useToggleSet<string>();
  const [nameFilter, setNameFilter] = useState('');
  const purge = usePurgeUnacked();

  const onRestore = (entry: UnackedEntry) => {
    if (!entry.delivery_tag) return;
    const confirmed = window.confirm(
      `Restore "${entry.task}" onto "${entry.queue ?? '?'}" (in-flight ${
        entry.age_display ?? '?'
      })?\n\n` +
        'Restoring re-publishes the task onto its routing-key queue. Only do this if ' +
        'a worker is no longer holding it — restoring an entry a worker is still running ' +
        'causes a duplicate execution.'
    );
    if (confirmed) restore.mutate([entry.delivery_tag]);
  };

  const onRemove = (entry: UnackedEntry) => {
    if (!entry.delivery_tag) return;
    const confirmed = window.confirm(
      `Remove "${entry.task}" (in-flight ${entry.age_display ?? '?'}) from the unacked ` +
        `set without re-running it?\n\n` +
        'The task is discarded — no worker will pick it up again. Use this for poison ' +
        'messages or work that is no longer relevant.'
    );
    if (confirmed) remove.mutate([entry.delivery_tag]);
  };

  const toggleExpanded = (tag: string | null) => {
    if (tag) expanded.toggle(tag);
  };

  if (isLoading && !data) return <Loading />;
  if (error) return <PanelError title="Unacked" error={error} />;

  // Primary sort: age ascending (newest at top, long-runners at the bottom).
  // Tiebreak by delivery_tag so entries with the same age don't swap places
  // poll-over-poll just because HGETALL hands them back in a different order.
  const byAgeAscThenTag = (a: UnackedEntry, b: UnackedEntry) => {
    const ageDelta = (a.age_seconds ?? 0) - (b.age_seconds ?? 0);
    if (ageDelta !== 0) return ageDelta;
    return (a.delivery_tag ?? '').localeCompare(b.delivery_tag ?? '');
  };
  const all = (data ?? []).slice().sort(byAgeAscThenTag);
  const trimmed = nameFilter.trim().toLowerCase();
  const entries = all.filter((entry) => {
    if (trimmed && !(entry.task ?? '').toLowerCase().includes(trimmed)) return false;
    if (queues.values.size > 0 && !queues.values.has(entry.queue ?? '')) return false;
    return true;
  });
  const disabled = restore.isPending || remove.isPending || purge.isPending;
  const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

  // Only ever act on rows that are both selected AND currently visible. A
  // selection made before narrowing the filter stays remembered, but can't be
  // acted on while it's off screen — you should never remove what you can't see.
  const visibleTags = entries
    .map((entry) => entry.delivery_tag)
    .filter((tag): tag is string => tag !== null);
  const selectedTags = visibleTags.filter((tag) => selected.values.has(tag));
  const allVisibleSelected = visibleTags.length > 0 && selectedTags.length === visibleTags.length;
  const taskPaths = new Set(
    entries
      .filter((entry) => entry.delivery_tag && selected.values.has(entry.delivery_tag))
      .map((entry) => entry.task ?? '')
  );
  const soleTaskPath = taskPaths.size === 1 ? [...taskPaths][0] : null;

  const toggleAllVisible = () => selected.replace(allVisibleSelected ? [] : visibleTags);

  const onBulkRestore = () => {
    const confirmed = window.confirm(
      `Restore ${selectedTags.length} entries onto their own queues?\n\n` +
        'Each is re-published to the queue it came from. Any entry a worker is ' +
        'still running will execute a second time.'
    );
    if (confirmed) restore.mutate(selectedTags, { onSuccess: selected.clear });
  };

  const onBulkRemove = () => {
    const confirmed = window.confirm(
      `Remove ${selectedTags.length} entries without re-running them?\n\n` +
        'They are discarded — no worker picks them up again. This cannot be undone.'
    );
    if (confirmed) remove.mutate(selectedTags, { onSuccess: selected.clear });
  };

  const onPurgeTaskPath = () => {
    if (!soleTaskPath) return;
    const confirmed = window.confirm(
      `Purge EVERY unacked entry for "${soleTaskPath}"?\n\n` +
        `This goes beyond your ${selectedTags.length} selected row(s) — it removes all ` +
        'entries for this task path, including any hidden by the current filters. ' +
        'Nothing is re-published and this cannot be undone.'
    );
    if (confirmed) purge.mutate(soleTaskPath, { onSuccess: selected.clear });
  };

  return (
    <PanelCard
      title={`Unacked — ${entries.length === all.length ? all.length : `${entries.length} of ${all.length}`}`}>
      <div className="space-y-2 mb-3">
        <NameFilter
          value={nameFilter}
          onChange={setNameFilter}
          placeholder="Filter by task dot path…"
        />
        <BadgeFilterRow
          label="Queue"
          options={QUEUE_NAMES}
          selected={queues.values}
          onToggle={queues.toggle}
          onClear={queues.clear}
          renderBadge={(queue) => <QueueBadge queue={queue} />}
        />
      </div>

      {selectedTags.length > 0 && (
        <BulkActionBar
          count={selectedTags.length}
          disabled={disabled}
          taskPath={soleTaskPath}
          onRestore={onBulkRestore}
          onRemove={onBulkRemove}
          onPurgeTaskPath={onPurgeTaskPath}
          onClear={selected.clear}
        />
      )}

      {all.length === 0 ? (
        <p className="text-sm text-gray-500">None.</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-500">No unacked entries match these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 w-4">
                  <input
                    type="checkbox"
                    aria-label="Select all shown"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th className="py-2 w-4" />
                <th className="py-2">Task</th>
                <th className="py-2">Queue</th>
                <th className="py-2">Age</th>
                <th className="py-2">Delivery tag</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => {
                const tag = entry.delivery_tag ?? String(index);
                const isOpen = expanded.values.has(tag);
                const started = startedAtIso(entry.age_seconds);
                return (
                  <Fragment key={tag}>
                    <tr
                      className="border-t align-top cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleExpanded(entry.delivery_tag)}>
                      <td className="py-2" onClick={stopRowClick}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${entry.task ?? tag}`}
                          checked={selected.values.has(tag)}
                          onChange={() => selected.toggle(tag)}
                        />
                      </td>
                      <td className="py-2 text-xs text-gray-400">{isOpen ? '▼' : '▶'}</td>
                      <TaskCell name={entry.task} id={entry.id} />
                      <td className="py-2 whitespace-nowrap">
                        <QueueBadge queue={entry.queue} />
                      </td>
                      <td className="py-2 whitespace-nowrap" title={started ?? undefined}>
                        {entry.age_display ?? '—'}
                      </td>
                      <td className="py-2 font-mono text-xs text-gray-500">{entry.delivery_tag}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2" onClick={stopRowClick}>
                          <Tooltip
                            tooltipContent={`Re-publishes onto "${entry.queue ?? '?'}" — the task is re-enqueued and a worker will run it again.`}>
                            <Button
                              color={Color.BLUE}
                              disabled={disabled}
                              onClick={() => onRestore(entry)}>
                              <FontAwesomeIcon icon={faPaperPlane} />
                            </Button>
                          </Tooltip>
                          <Tooltip tooltipContent="Discards this entry from the unacked set without re-publishing — the task will not run again.">
                            <Button
                              color={Color.RED}
                              disabled={disabled}
                              onClick={() => onRemove(entry)}>
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                    {isOpen && <UnackedDetailRow entry={entry} colSpan={DETAIL_COL_SPAN} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PanelCard>
  );
};

export default UnackedPanel;
